import { useQuery, useQueryClient } from 'react-query'
import { agentService } from '../services/api'
import type { Agent } from '../types/agent'

/**
 * * Hook to fetch all agents
 */
export function useAgents() {
  return useQuery<Agent[], Error>(
    'agents',
    agentService.getAllAgents,
    {
      retry: 1,
      refetchOnWindowFocus: false,
    }
  )
}

/**
 * * Hook to fetch a single agent by ID
 */
export function useAgent(id: string | undefined) {
  return useQuery<Agent, Error>(
    ['agent', id],
    () => agentService.getAgentById(id!),
    {
      enabled: !!id,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  )
}

/**
 * * Hook to fetch active agents
 */
export function useActiveAgents(options?: { refetchInterval?: number }) {
  return useQuery<Agent[], Error>(
    'activeAgents',
    agentService.getActiveAgents,
    {
      refetchInterval: options?.refetchInterval || 30000, // * Default: 30 seconds
      retry: 1,
      refetchOnWindowFocus: false,
    }
  )
}

/**
 * * Hook to invalidate agents queries
 */
export function useInvalidateAgents() {
  const queryClient = useQueryClient()
  
  return {
    invalidateAll: () => {
      queryClient.invalidateQueries('agents')
      queryClient.invalidateQueries('activeAgents')
    },
    invalidateAgent: (id: string) => {
      queryClient.invalidateQueries(['agent', id])
    },
    invalidateActiveAgents: () => {
      queryClient.invalidateQueries('activeAgents')
    }
  }
}
